import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, Users, Music, Trash2, Edit2, DollarSign, 
  TrendingUp, CheckCircle, Loader2, UserMinus, Download,
  Banknote, History, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Paythroughs({ user, selectedArtist }) {
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [trackPaythroughs, setTrackPaythroughs] = useState([]);
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [labelArtists, setLabelArtists] = useState([]);
  const [selectedLabelArtist, setSelectedLabelArtist] = useState('');
  const [availableTracks, setAvailableTracks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  
  // Dialogs
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [trackPaythroughDialogOpen, setTrackPaythroughDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [batchTrackDialogOpen, setBatchTrackDialogOpen] = useState(false);
  
  // Forms
  const [recipientForm, setRecipientForm] = useState({ name: '', email: '', notes: '' });
  const [trackForm, setTrackForm] = useState({ track_name: '', isrc: '', recipient_id: '', percentage: '' });
  const [memberForm, setMemberForm] = useState({ 
    member_name: '', 
    member_type: 'current', 
    initial_recoupment_amount: '', 
    share_percentage: '25',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    recipient_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer',
    reference: '',
    notes: ''
  });
  const [incomeForm, setIncomeForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    source: 'royalties',
    description: '',
    notes: ''
  });
  const [batchForm, setBatchForm] = useState({
    recipient_id: '',
    percentage: '',
    selectedTracks: []
  });
  
  // Editing
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [editingMember, setEditingMember] = useState(null);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const fetchLabelArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/label/artists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabelArtists(response.data);
      
      // Auto-select first artist or use selectedArtist
      if (response.data.length > 0) {
        const artistToSelect = selectedArtist && selectedArtist !== 'all' && selectedArtist !== 'management'
          ? selectedArtist
          : response.data[0].id;
        setSelectedLabelArtist(artistToSelect);
      }
    } catch (error) {
      console.error('Failed to fetch label artists:', error);
    }
  }, [selectedArtist]);

  const fetchData = useCallback(async () => {
    if (!selectedLabelArtist) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all data in parallel
      const [recipientsRes, trackPaythroughsRes, membersRes, summaryRes, tracksRes, paymentsRes, incomeRes] = await Promise.all([
        axios.get(`${API}/label/paythrough-recipients?artist_id=${selectedLabelArtist}`, { headers }),
        axios.get(`${API}/label/track-paythroughs?artist_id=${selectedLabelArtist}`, { headers }),
        axios.get(`${API}/label/member-recoupment?artist_id=${selectedLabelArtist}`, { headers }),
        axios.get(`${API}/label/paythrough-summary/${selectedLabelArtist}`, { headers }),
        // Get available tracks from DSR data
        axios.get(`${API}/royalties/artists/${encodeURIComponent(labelArtists.find(a => a.id === selectedLabelArtist)?.name || '')}`, { headers }).catch(() => ({ data: { tracks: [] } })),
        axios.get(`${API}/label/paythrough-payments?artist_id=${selectedLabelArtist}`, { headers }),
        axios.get(`${API}/label/recoupment-income?artist_id=${selectedLabelArtist}`, { headers })
      ]);
      
      setRecipients(recipientsRes.data);
      setTrackPaythroughs(trackPaythroughsRes.data);
      setMembers(membersRes.data);
      setSummary(summaryRes.data);
      setAvailableTracks(tracksRes.data?.tracks || []);
      setPayments(paymentsRes.data);
      setIncomeRecords(incomeRes.data);
    } catch (error) {
      console.error('Failed to fetch paythrough data:', error);
      toast.error('Failed to load paythrough data');
    } finally {
      setLoading(false);
    }
  }, [selectedLabelArtist, labelArtists]);

  useEffect(() => {
    fetchLabelArtists();
  }, [fetchLabelArtists]);

  useEffect(() => {
    if (selectedLabelArtist && labelArtists.length > 0) {
      fetchData();
    }
  }, [selectedLabelArtist, labelArtists, fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Recipient handlers
  const handleSaveRecipient = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingRecipient) {
        await axios.put(`${API}/label/paythrough-recipients/${editingRecipient.id}`, recipientForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipient updated');
      } else {
        await axios.post(`${API}/label/paythrough-recipients`, {
          ...recipientForm,
          artist_id: selectedLabelArtist
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipient added');
      }
      
      setRecipientDialogOpen(false);
      setRecipientForm({ name: '', email: '', notes: '' });
      setEditingRecipient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipient');
    }
  };

  const handleDeleteRecipient = async (id) => {
    if (!confirm('Delete this recipient? Their track paythroughs will also be deleted.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/paythrough-recipients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recipient deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete recipient');
    }
  };

  // Track paythrough handlers
  const handleSaveTrackPaythrough = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingTrack) {
        await axios.put(`${API}/label/track-paythroughs/${editingTrack.id}`, {
          ...trackForm,
          percentage: parseFloat(trackForm.percentage)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Track paythrough updated');
      } else {
        await axios.post(`${API}/label/track-paythroughs`, {
          ...trackForm,
          artist_id: selectedLabelArtist,
          percentage: parseFloat(trackForm.percentage)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Track paythrough added');
      }
      
      setTrackPaythroughDialogOpen(false);
      setTrackForm({ track_name: '', isrc: '', recipient_id: '', percentage: '' });
      setEditingTrack(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save track paythrough');
    }
  };

  const handleDeleteTrackPaythrough = async (id) => {
    if (!confirm('Delete this track paythrough?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/track-paythroughs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Track paythrough deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete track paythrough');
    }
  };

  // Member recoupment handlers
  const handleSaveMember = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingMember) {
        await axios.put(`${API}/label/member-recoupment/${editingMember.id}`, {
          ...memberForm,
          initial_recoupment_amount: parseFloat(memberForm.initial_recoupment_amount) || 0,
          share_percentage: parseFloat(memberForm.share_percentage) || 25
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Member updated');
      } else {
        await axios.post(`${API}/label/member-recoupment`, {
          ...memberForm,
          artist_id: selectedLabelArtist,
          initial_recoupment_amount: parseFloat(memberForm.initial_recoupment_amount) || 0,
          share_percentage: parseFloat(memberForm.share_percentage) || 25
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Member added');
      }
      
      setMemberDialogOpen(false);
      setMemberForm({ member_name: '', member_type: 'current', initial_recoupment_amount: '', share_percentage: '25', notes: '' });
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save member');
    }
  };

  const handleDeleteMember = async (id) => {
    if (!confirm('Delete this member?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/member-recoupment/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  // Payment handlers
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/label/paythrough-payments`, {
        ...paymentForm,
        artist_id: selectedLabelArtist,
        amount: parseFloat(paymentForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentForm({
        recipient_id: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer',
        reference: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeletePayment = async (id) => {
    if (!confirm('Delete this payment record?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/paythrough-payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  // Income handlers
  const handleRecordIncome = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/label/recoupment-income`, {
        ...incomeForm,
        artist_id: selectedLabelArtist,
        amount: parseFloat(incomeForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Income recorded and distributed to member recoupment');
      setIncomeDialogOpen(false);
      setIncomeForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        source: 'royalties',
        description: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record income');
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!confirm('Delete this income record? Recoupment allocations will be reversed.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/recoupment-income/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Income record deleted and recoupment reversed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete income record');
    }
  };

  // Sync DSR royalties to recoupment
  const [syncing, setSyncing] = useState(false);
  const handleSyncDSR = async () => {
    if (!confirm('This will sync all DSR royalties to member recoupment balances. Continue?')) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/label/sync-dsr-recoupment/${selectedLabelArtist}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      toast.success(`Synced ${formatCurrency(data.amount_applied)} to ${data.members_updated} members`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync DSR to recoupment');
    } finally {
      setSyncing(false);
    }
  };

  // Download member statement PDF
  const handleDownloadStatement = async (memberId, memberName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/label/member-statement/${memberId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${memberName.replace(/\s+/g, '_')}_Statement.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (error) {
      toast.error('Failed to download statement');
    }
  };

  // Batch track paythrough handler
  const handleBatchTrackPaythrough = async (e) => {
    e.preventDefault();
    if (batchForm.selectedTracks.length === 0) {
      toast.error('Please select at least one track');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const result = await axios.post(`${API}/label/track-paythroughs/batch`, {
        artist_id: selectedLabelArtist,
        track_names: batchForm.selectedTracks,
        recipient_id: batchForm.recipient_id,
        percentage: parseFloat(batchForm.percentage)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Created ${result.data.created} track paythroughs${result.data.skipped > 0 ? `, skipped ${result.data.skipped} (already exist)` : ''}`);
      setBatchTrackDialogOpen(false);
      setBatchForm({ recipient_id: '', percentage: '', selectedTracks: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch paythroughs');
    }
  };

  // Export handler
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/label/paythrough-report/${selectedLabelArtist}/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paythrough_report_${summary?.artist_name?.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  // Calculate total paid to each recipient
  const getTotalPaid = (recipientId) => {
    return payments
      .filter(p => p.recipient_id === recipientId)
      .reduce((sum, p) => sum + p.amount, 0);
  };

  if (loading && !selectedLabelArtist) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="paythroughs-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold font-manrope">Paythroughs</h1>
          <p className="text-muted-foreground text-sm">Manage leaving member paythroughs and current member recoupment</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Export button */}
          {isManager && summary && (
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
          )}
          
          {/* Artist selector */}
          <select
            value={selectedLabelArtist}
            onChange={(e) => setSelectedLabelArtist(e.target.value)}
            className="bg-input border border-border rounded-lg px-3 py-2 text-sm"
            data-testid="artist-selector"
          >
            {labelArtists.map(artist => (
              <option key={artist.id} value={artist.id}>{artist.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 p-5" data-testid="paythrough-summary">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-400" />
            Royalty Breakdown for {summary.artist_name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Gross Revenue</p>
              <p className="text-lg font-bold">{formatCurrency(summary.gross_revenue)}</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Virgin 25% Fee</p>
              <p className="text-lg font-bold text-red-400">-{formatCurrency(summary.virgin_fee)}</p>
            </div>
            <div className="bg-amber-500/10 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Paythroughs Owed</p>
              <p className="text-lg font-bold text-amber-400">-{formatCurrency(summary.total_paythroughs)}</p>
            </div>
            <div className="bg-green-500/10 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Remaining for Recoup</p>
              <p className="text-lg font-bold text-green-400">{formatCurrency(summary.remaining_after_paythroughs)}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Leaving Members (Paythrough Recipients) */}
        <Card className="p-5" data-testid="recipients-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <UserMinus className="w-4 h-4 text-amber-400" />
              Leaving Members
            </h3>
            {isManager && (
              <Button
                size="sm"
                onClick={() => {
                  setEditingRecipient(null);
                  setRecipientForm({ name: '', email: '', notes: '' });
                  setRecipientDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          </div>
          
          {recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No leaving members configured</p>
          ) : (
            <div className="space-y-3">
              {recipients.map(recipient => {
                const recipientSummary = summary?.recipients?.find(r => r.name === recipient.name);
                const totalPaid = getTotalPaid(recipient.id);
                const totalOwed = recipientSummary?.total_owed || 0;
                const remaining = totalOwed - totalPaid;
                
                return (
                  <div key={recipient.id} className="bg-muted/30 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{recipient.name}</p>
                        {recipient.email && <p className="text-xs text-muted-foreground">{recipient.email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {isManager && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingRecipient(recipient);
                                setRecipientForm({
                                  name: recipient.name,
                                  email: recipient.email || '',
                                  notes: recipient.notes || ''
                                });
                                setRecipientDialogOpen(true);
                              }}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteRecipient(recipient.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Payment status */}
                    <div className="mt-2 pt-2 border-t border-border/50 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <p className="text-muted-foreground">Owed</p>
                        <p className="text-amber-400 font-medium">{formatCurrency(totalOwed)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Paid</p>
                        <p className="text-green-400 font-medium">{formatCurrency(totalPaid)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Remaining</p>
                        <p className={`font-medium ${remaining > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {formatCurrency(remaining)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Record payment button */}
                    {isManager && remaining > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full mt-2 text-xs"
                        onClick={() => {
                          setPaymentForm({
                            ...paymentForm,
                            recipient_id: recipient.id,
                            amount: remaining.toFixed(2)
                          });
                          setPaymentDialogOpen(true);
                        }}
                      >
                        <Banknote className="w-3 h-3 mr-1" />
                        Record Payment
                      </Button>
                    )}
                    
                    {recipientSummary?.tracks?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <p className="text-xs text-muted-foreground mb-1">Tracks:</p>
                        {recipientSummary.tracks.map((track, i) => (
                          <div key={track.track_name || i} className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{track.track_name} ({track.percentage}%)</span>
                            <span className="text-amber-400">{formatCurrency(track.paythrough_amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Current Members (Recoupment) */}
        <Card className="p-5" data-testid="members-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-green-400" />
              Current Members (Recoupment)
            </h3>
            {isManager && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSyncDSR}
                  disabled={members.length === 0 || syncing}
                  data-testid="sync-dsr-btn"
                >
                  {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
                  Sync DSR
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIncomeDialogOpen(true)}
                  disabled={members.length === 0}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Record Income
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingMember(null);
                    setMemberForm({ member_name: '', member_type: 'current', initial_recoupment_amount: '', share_percentage: '25', notes: '' });
                    setMemberDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            )}
          </div>
          
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No current members configured</p>
          ) : (
            <div className="space-y-3">
              {members.map(member => {
                const memberSummary = summary?.members?.find(m => m.id === member.id);
                const isRecouped = memberSummary?.fully_recouped;
                return (
                  <div key={member.id} className={`rounded-lg p-3 ${isRecouped ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted/30'}`} data-testid={`member-card-${member.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {member.member_name}
                          {member.member_type === 'longstanding' && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Longstanding</span>
                          )}
                          {isRecouped && <CheckCircle className="w-4 h-4 text-green-400" />}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {member.share_percentage}% share after recoupment
                        </p>
                      </div>
                      {isManager && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Download Statement"
                            onClick={() => handleDownloadStatement(member.id, member.member_name)}
                            data-testid={`download-statement-${member.id}`}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingMember(member);
                              setMemberForm({
                                member_name: member.member_name,
                                member_type: member.member_type || 'current',
                                initial_recoupment_amount: member.initial_recoupment_amount?.toString() || '',
                                share_percentage: member.share_percentage?.toString() || '25',
                                notes: member.notes || ''
                              });
                              setMemberDialogOpen(true);
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleDeleteMember(member.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {memberSummary && (
                      <div className="mt-2 pt-2 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">To Recoup</p>
                          <p className={memberSummary.remaining_to_recoup > 0 ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                            {formatCurrency(memberSummary.remaining_to_recoup)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Potential Share</p>
                          <p className="text-green-400 font-medium">{formatCurrency(memberSummary.potential_share)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Track Paythroughs */}
      <Card className="p-5" data-testid="track-paythroughs-section">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Music className="w-4 h-4 text-purple-400" />
            Track Paythrough Configurations
          </h3>
          {isManager && recipients.length > 0 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setBatchForm({ recipient_id: '', percentage: '', selectedTracks: [] });
                  setBatchTrackDialogOpen(true);
                }}
                disabled={availableTracks.length === 0}
              >
                <FileText className="w-4 h-4 mr-1" />
                Batch Add
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditingTrack(null);
                  setTrackForm({ track_name: '', isrc: '', recipient_id: '', percentage: '' });
                  setTrackPaythroughDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Track
              </Button>
            </div>
          )}
        </div>
        
        {recipients.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Add a leaving member first to configure track paythroughs</p>
        ) : trackPaythroughs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No track paythroughs configured</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Track</th>
                  <th className="text-left py-2 px-3">ISRC</th>
                  <th className="text-left py-2 px-3">Recipient</th>
                  <th className="text-right py-2 px-3">%</th>
                  {isManager && <th className="text-right py-2 px-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {trackPaythroughs.map(tp => {
                  const recipient = recipients.find(r => r.id === tp.recipient_id);
                  return (
                    <tr key={tp.id} className="border-b border-border/50">
                      <td className="py-2 px-3">{tp.track_name}</td>
                      <td className="py-2 px-3 text-muted-foreground">{tp.isrc || '-'}</td>
                      <td className="py-2 px-3">{recipient?.name || 'Unknown'}</td>
                      <td className="py-2 px-3 text-right text-amber-400 font-medium">{tp.percentage}%</td>
                      {isManager && (
                        <td className="py-2 px-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingTrack(tp);
                              setTrackForm({
                                track_name: tp.track_name,
                                isrc: tp.isrc || '',
                                recipient_id: tp.recipient_id,
                                percentage: tp.percentage.toString()
                              });
                              setTrackPaythroughDialogOpen(true);
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400"
                            onClick={() => handleDeleteTrackPaythrough(tp.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment History */}
      {payments.length > 0 && (
        <Card className="p-5" data-testid="payment-history">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-green-400" />
            Payment History
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Recipient</th>
                  <th className="text-right py-2 px-3">Amount</th>
                  <th className="text-left py-2 px-3">Method</th>
                  <th className="text-left py-2 px-3">Reference</th>
                  {isManager && <th className="text-right py-2 px-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => {
                  const recipient = recipients.find(r => r.id === payment.recipient_id);
                  return (
                    <tr key={payment.id} className="border-b border-border/50">
                      <td className="py-2 px-3">{payment.date}</td>
                      <td className="py-2 px-3">{recipient?.name || 'Unknown'}</td>
                      <td className="py-2 px-3 text-right text-green-400 font-medium">{formatCurrency(payment.amount)}</td>
                      <td className="py-2 px-3 text-muted-foreground">{payment.payment_method || '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{payment.reference || '-'}</td>
                      {isManager && (
                        <td className="py-2 px-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Income History */}
      {incomeRecords.length > 0 && (
        <Card className="p-5" data-testid="income-history">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-blue-400" />
            Income History (Recoupment)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Source</th>
                  <th className="text-right py-2 px-3">Amount</th>
                  <th className="text-left py-2 px-3">Allocations</th>
                  {isManager && <th className="text-right py-2 px-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {incomeRecords.map(income => (
                  <tr key={income.id} className="border-b border-border/50">
                    <td className="py-2 px-3">{income.date}</td>
                    <td className="py-2 px-3">{income.source}</td>
                    <td className="py-2 px-3 text-right text-blue-400 font-medium">{formatCurrency(income.amount)}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {income.member_allocations?.map((a, i) => (
                        <span key={i} className="mr-2">
                          {a.member_name}: {formatCurrency(a.applied_to_recoupment)} recoup
                        </span>
                      ))}
                    </td>
                    {isManager && (
                      <td className="py-2 px-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400"
                          onClick={() => handleDeleteIncome(income.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recipient Dialog */}
      <Dialog open={recipientDialogOpen} onOpenChange={setRecipientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecipient ? 'Edit' : 'Add'} Leaving Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRecipient} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={recipientForm.name}
                onChange={(e) => setRecipientForm({...recipientForm, name: e.target.value})}
                placeholder="Member name"
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={recipientForm.email}
                onChange={(e) => setRecipientForm({...recipientForm, email: e.target.value})}
                placeholder="member@email.com"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={recipientForm.notes}
                onChange={(e) => setRecipientForm({...recipientForm, notes: e.target.value})}
                placeholder="Any notes about this member"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setRecipientDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1">{editingRecipient ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Track Paythrough Dialog */}
      <Dialog open={trackPaythroughDialogOpen} onOpenChange={setTrackPaythroughDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTrack ? 'Edit' : 'Add'} Track Paythrough</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTrackPaythrough} className="space-y-4">
            <div>
              <Label>Track Name *</Label>
              {availableTracks.length > 0 ? (
                <select
                  value={trackForm.track_name}
                  onChange={(e) => {
                    const track = availableTracks.find(t => t.track_title === e.target.value);
                    setTrackForm({
                      ...trackForm,
                      track_name: e.target.value,
                      isrc: track?.isrc || ''
                    });
                  }}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select a track</option>
                  {availableTracks.map((track, i) => (
                    <option key={i} value={track.track_title}>{track.track_title}</option>
                  ))}
                </select>
              ) : (
                <Input
                  value={trackForm.track_name}
                  onChange={(e) => setTrackForm({...trackForm, track_name: e.target.value})}
                  placeholder="Track name (must match DSR)"
                  required
                />
              )}
            </div>
            <div>
              <Label>ISRC (optional)</Label>
              <Input
                value={trackForm.isrc}
                onChange={(e) => setTrackForm({...trackForm, isrc: e.target.value})}
                placeholder="ISRC code for precise matching"
              />
            </div>
            <div>
              <Label>Recipient *</Label>
              <select
                value={trackForm.recipient_id}
                onChange={(e) => setTrackForm({...trackForm, recipient_id: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select recipient</option>
                {recipients.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Percentage (of net after Virgin 25%) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={trackForm.percentage}
                onChange={(e) => setTrackForm({...trackForm, percentage: e.target.value})}
                placeholder="e.g., 15"
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setTrackPaythroughDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1">{editingTrack ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Dialog */}
      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Edit' : 'Add'} Current Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveMember} className="space-y-4">
            <div>
              <Label>Member Name *</Label>
              <Input
                value={memberForm.member_name}
                onChange={(e) => setMemberForm({...memberForm, member_name: e.target.value})}
                placeholder="Member name"
                required
              />
            </div>
            <div>
              <Label>Member Type</Label>
              <select
                value={memberForm.member_type}
                onChange={(e) => setMemberForm({...memberForm, member_type: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
              >
                <option value="current">Current (New Member)</option>
                <option value="longstanding">Longstanding (Recoups Faster)</option>
              </select>
            </div>
            <div>
              <Label>Initial Recoupment Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={memberForm.initial_recoupment_amount}
                onChange={(e) => setMemberForm({...memberForm, initial_recoupment_amount: e.target.value})}
                placeholder="Amount they need to recoup before royalties"
              />
            </div>
            <div>
              <Label>Share Percentage (after recoupment) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={memberForm.share_percentage}
                onChange={(e) => setMemberForm({...memberForm, share_percentage: e.target.value})}
                placeholder="e.g., 25"
                required
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={memberForm.notes}
                onChange={(e) => setMemberForm({...memberForm, notes: e.target.value})}
                placeholder="Any notes"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setMemberDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1">{editingMember ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-400" />
              Record Payment to Leaving Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Recipient *</Label>
              <select
                value={paymentForm.recipient_id}
                onChange={(e) => setPaymentForm({...paymentForm, recipient_id: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select recipient</option>
                {recipients.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                placeholder="Payment amount"
                required
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={paymentForm.date}
                onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <select
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
              >
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="PayPal">PayPal</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                placeholder="Payment reference number"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Any notes"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">Record Payment</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Income Dialog */}
      <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              Record Recoupment Income
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordIncome} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This income will be distributed to current members based on their share percentages and applied toward their recoupment.
            </p>
            <div>
              <Label>Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={incomeForm.amount}
                onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})}
                placeholder="Income amount"
                required
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={incomeForm.date}
                onChange={(e) => setIncomeForm({...incomeForm, date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Source</Label>
              <select
                value={incomeForm.source}
                onChange={(e) => setIncomeForm({...incomeForm, source: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
              >
                <option value="royalties">Royalties</option>
                <option value="sync">Sync Licensing</option>
                <option value="merch">Merchandise</option>
                <option value="live">Live Performance</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={incomeForm.description}
                onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})}
                placeholder="e.g., Q3 2026 Streaming Royalties"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIncomeDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Record Income</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Track Paythrough Dialog */}
      <Dialog open={batchTrackDialogOpen} onOpenChange={setBatchTrackDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Batch Add Track Paythroughs
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBatchTrackPaythrough} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select multiple tracks to apply the same paythrough percentage to a recipient.
            </p>
            <div>
              <Label>Recipient *</Label>
              <select
                value={batchForm.recipient_id}
                onChange={(e) => setBatchForm({...batchForm, recipient_id: e.target.value})}
                className="w-full bg-input border border-border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select recipient</option>
                {recipients.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Percentage (of net after Virgin 25%) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={batchForm.percentage}
                onChange={(e) => setBatchForm({...batchForm, percentage: e.target.value})}
                placeholder="e.g., 15"
                required
              />
            </div>
            <div>
              <Label>Select Tracks ({batchForm.selectedTracks.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto border border-border rounded-lg p-2 mt-1 space-y-1">
                {availableTracks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No tracks available from DSR data</p>
                ) : (
                  [...new Set(availableTracks.map(t => t.track_title))].map(trackName => (
                    <label key={trackName} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={batchForm.selectedTracks.includes(trackName)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBatchForm({...batchForm, selectedTracks: [...batchForm.selectedTracks, trackName]});
                          } else {
                            setBatchForm({...batchForm, selectedTracks: batchForm.selectedTracks.filter(t => t !== trackName)});
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{trackName}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setBatchTrackDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={batchForm.selectedTracks.length === 0}>
                Add {batchForm.selectedTracks.length} Track{batchForm.selectedTracks.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
